import {useMemo} from 'react'
import {
  ActionButton,
  BreakStyle,
  convertToAnalyticsString,
  defineStylesWithBreaks,
  GLTranslate,
  type PartialRecord,
  themeIsDark,
  useGLTranslation,
  useTheme,
  useThemedStylesWithBreaks,
  type AnalyticsValue
} from 'notability-services-common-frontend'
import {AnalyticsParameterName, useExperimentValues, useSubscriptionTier} from 'notability-services-next-frontend'
import {proxima} from '../fonts'
import {SubscriptionFeatureList} from './SubscriptionFeatureList'
import {addingBasePath} from '../config'
import {useCustomRouter} from '../util/useCustomRouter'
import {SubscriptionTier, TranslationKey} from 'notability-services-common-universal'
import {type NotabilityPrice} from '../util/types'

type TierData = {
  features: TranslationKey[]
  limitationLine?: TranslationKey
}

const consumerTierData: PartialRecord<string, TierData> = {
  starter: {
    features: ['consumerFeatureUnlimitedNotes', 'consumerFeaturePdfAnnotation', 'consumerFeatureRecordAudio']
  },
  premium: {
    features: [
      'consumerFeatureTranscription',
      'consumerFeatureLearnSummary',
      'consumerFeatureLearnQuizzes',
      'consumerFeatureUnlimitedNotes',
      'consumerFeaturePdfAnnotation',
      'consumerFeatureRecordAudio'
    ],
    limitationLine: 'consumerPlusLimitations'
  },
  pro: {
    features: [
      'consumerFeatureTranscription',
      'consumerFeatureLearnSummary',
      'consumerFeatureLearnQuizzes',
      'consumerFeatureUnlimitedNotes',
      'consumerFeaturePdfAnnotation',
      'consumerFeatureRecordAudio'
    ],
    limitationLine: 'consumerProLimitations'
  }
}

const formatCurency = (amount: number, currency: string, omitCentsIfZero: boolean = true) => {
  const dollars = Math.floor(amount / 100)
  const cents = amount - dollars * 100
  const numberString = `${cents === 0 && omitCentsIfZero ? dollars : `${(amount / 100).toFixed(2)}`}`

  if (currency === 'usd') {
    return `$${numberString}`
  } else {
    return `${numberString}${currency}`
  }
}

export function getStripePriceAnalyticsParams(
  price: NotabilityPrice
): Partial<Record<AnalyticsParameterName, AnalyticsValue>> {
  const metadata = Object.entries(price.metadata).reduce((acc, [key, value]) => {
    acc[convertToAnalyticsString(key)] = convertToAnalyticsString(value)
    return acc
  }, {} as Record<string, string>)
  return {
    ...metadata,
    'Stripe Price ID': price.stripeId,
    'Subscription Interval': price.intervalUnit
      ? convertToAnalyticsString(`${price.intervalCount} ${price.intervalUnit}`)
      : undefined,
    'Subscription Price': price.centsPerInterval ? price.centsPerInterval / 100 : undefined
  }
}

export function SubscriptionCard({
  tier,
  intervalUnit,
  centsPerInterval,
  currency,
  stripeId,
  portalUrl
}: NotabilityPrice) {
  const s = useThemedStylesWithBreaks(styles)
  const {t} = useGLTranslation()
  const router = useCustomRouter()
  const experimentValues = useExperimentValues()
  const formattedPrice = useMemo(() => formatCurency(centsPerInterval, currency), [centsPerInterval, currency])
  const {theme} = useTheme()
  const currentTier = useSubscriptionTier(true)
  const tierLogo = useMemo(() => {
    if (tier === SubscriptionTier.pro) {
      return addingBasePath(themeIsDark(theme.name) ? '/pro-dark.svg' : '/pro-light.svg')
    } else if (tier === SubscriptionTier.premium) {
      return addingBasePath(themeIsDark(theme.name) ? '/plus-dark.svg' : '/plus-light.svg')
    } else {
      return null
    }
  }, [theme, tier])

  const headerStyle = useMemo(() => {
    if (!experimentValues.subscriptionEnableMonthlyAndPro) {
      return s.cardHeaderSpaceHolder
    }

    return tier === SubscriptionTier.premium ? s.cardHeader : s.cardHeaderSpaceHolder
  }, [experimentValues, s, tier])

  const tierData: TierData | undefined = useMemo(() => {
    return consumerTierData[tier]
  }, [tier])

  const buttonText = useMemo<TranslationKey>(() => {
    if (currentTier === tier) {
      return portalUrl ? 'manageSubscription' : 'consumerCurrentPlan'
    } else {
      return 'consumerPlanSelectionCTA'
    }
  }, [currentTier, tier, portalUrl])

  if (!tierData) {
    return null
  }

  return (
    <div style={s.card}>
      <div style={headerStyle}>{t('consumerMostPopular')}</div>
      <div style={s.contentContainer}>
        <div style={s.upperContainer}>
          {tier === SubscriptionTier.starter ? <div style={s.planTitle}>{t('consumerFreeTitle')}</div> : null}
          {tierLogo ? <img style={s.plusLogo} alt="plus" src={tierLogo} /> : null}
          <div style={s.priceLine}>
            {centsPerInterval === 0 ? (
              <div style={s.price}>{t('pricingFreeSubHeader')}</div>
            ) : (
              <GLTranslate
                i18nKey={intervalUnit === 'month' ? 'consumerPricePerMonth' : 'consumerPricePerYear'}
                values={{
                  price: formattedPrice
                }}
              >
                <span style={s.price}>$25</span> / yr
              </GLTranslate>
            )}
          </div>
          {tierData.limitationLine ? (
            <div style={s.limitationLine}>
              <GLTranslate
                i18nKey={tierData.limitationLine}
                values={{
                  productName: 'Learn'
                }}
              >
                <span style={s.learnTitle}>Learn</span> (Some Limitations Apply)
              </GLTranslate>
            </div>
          ) : null}
          <SubscriptionFeatureList features={tierData.features} />
        </div>
        <ActionButton
          style={s.upgradeButton}
          text={t(buttonText)}
          disabled={currentTier === tier && !portalUrl}
          appearance={currentTier === tier && portalUrl ? 'stroked' : 'filled'}
          size={'small'}
          onClick={() => {
            if (portalUrl) {
              window.location.href = portalUrl
            } else {
              router.pushPlanSubscription(stripeId)
            }
          }}
        />
      </div>
    </div>
  )
}

export const commonSubscriptionCardStyles: BreakStyle = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  width: '413px',
  border: `1.25px solid`,
  borderRadius: '16px',
  fontFamily: proxima.style.fontFamily,
  maxWidth: 'calc(100vw - 24px)',
  overflow: 'hidden',
  justifyContent: 'space-between'
}

const cardHeaderBase: BreakStyle = {
  padding: '8px 0',
  width: '100%'
}

const upgradeButton: BreakStyle = {
  width: '100%',
  height: '44px',
  marginTop: '28px',
  textTransform: 'capitalize'
}

const styles = defineStylesWithBreaks((theme, font) => {
  return {
    card: {
      ...commonSubscriptionCardStyles,
      ...theme.elevation.level3,
      borderColor: theme.core.f1,
      backgroundColor: theme.core.b1
    },

    starterCard: {
      ...commonSubscriptionCardStyles,
      backgroundColor: theme.core.b1,
      borderColor: theme.core.b4
    },

    cardHeader: {
      ...cardHeaderBase,
      backgroundColor: theme.core.f1,
      color: theme.core.b1,
      textAlign: 'center',
      textTransform: 'uppercase'
    },

    cardHeaderSpaceHolder: {
      ...cardHeaderBase,
      visibility: 'hidden'
    },

    contentContainer: {
      padding: '10px 24px 32px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      justifyContent: 'space-between'
    },

    freeInnerContent: {
      width: '100%'
    },

    planTitle: {
      ...font.mediumTitle,
      backgroundColor: theme.core.b2,
      borderRadius: '8px',
      fontSize: '23px',
      color: theme.core.f3,
      margin: '2px 0',
      padding: '0 16px',
      width: 'fit-content'
    },

    currentPlan: {
      ...font.subheadBold,
      color: theme.core.f2,
      backgroundColor: theme.accent.container,
      padding: '8px',
      borderRadius: '8px',
      border: `1px solid ${theme.accent.containerHover}`,
      width: '100%'
    },

    starterCardContent: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: '1',
      gap: '12px'
    },

    separatorContainer: {
      ...font.smallTitleBold,
      color: theme.secondary.base,
      display: 'flex',
      flexDirection: 'row',
      gap: '12px'
    },

    separatorBar: {
      borderTop: `1px solid ${theme.secondary.base}`,
      height: '1px',
      width: '100%'
    },

    cashCallout: {
      fontSize: '48px'
    },

    plusLogo: {
      height: '40px'
    },

    learnTitle: {
      fontWeight: 700
    },

    limitationLine: {
      width: '100%',
      marginBottom: '17px',
      fontSize: '22px'
    },

    price: {
      ...font.largeTitleBold,
      fontSize: '42px',
      fontWeight: 700
    },

    priceLine: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'center',
      gap: '8px',
      margin: '10px'
    },

    upgradeButton: {
      ...upgradeButton
    },

    currentPlanContainer: {
      ...upgradeButton,
      ...font.bodyBold,
      alignItems: 'center',
      backgroundColor: theme.neutral.container,
      borderRadius: '8px',
      color: theme.neutral.base,
      display: 'flex',
      height: '44px',
      justifyContent: 'center',
      marginTop: '28px',
      padding: '12px 16px'
    },

    upperContainer: {
      width: '100%',
      justifyItems: 'center'
    }
  }
})
